import { cn } from "cn";
import { HardHat, Layers, type LucideIcon, Wrench } from "lucide-react";

import { Badge, type BadgeProps } from "@/components/ui/badge/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  isListItemWorkType,
  type ListItemWorkTypeValue,
  WORK_TYPE_LABELS,
} from "./list-item-field-labels";

type WorkTypeSpec = {
  label: string;
  Icon: LucideIcon;
  color: NonNullable<BadgeProps["color"]>;
  /**
   * `color` as a text colour, for the icon shown without its chip; the chip's own icon takes the
   * badge's colour. The member order status spec pairs its two colours the same way.
   */
  iconColor: string;
  /** What an item of this type is for, in one sentence: the type's tooltip. */
  meaning: string;
};

/**
 * How each work type shows. On its chip and in the side panel's Type row: its name, an icon and a
 * colour together, never the colour alone, so the three read apart at a glance. The office Lists
 * tab's Type filter takes the name and the icon in its colour (`iconColor`). A type never takes a
 * status badge's colour (To do fuchsia, In progress purple, Done green, Archived black) or one
 * beside it: Project is rose, as indigo read like In progress. Red reads as failure, blue is the
 * member's "Part of ORD-…" order badge (so Service is not blue either), amber sits beside orange,
 * pink beside fuchsia, and yellow and lime words are too faint on their tints.
 */
export const WORK_TYPE_SPECS: Record<ListItemWorkTypeValue, WorkTypeSpec> = {
  PROJECT: {
    label: WORK_TYPE_LABELS.PROJECT,
    Icon: Layers,
    color: "rose",
    iconColor: "text-text-rose",
    meaning: "Several trades, permits, design or coordination.",
  },
  SERVICE: {
    label: WORK_TYPE_LABELS.SERVICE,
    Icon: HardHat,
    color: "cyan",
    iconColor: "text-text-cyan",
    meaning: "A visit by a trade or a specialist.",
  },
  FIX: {
    label: WORK_TYPE_LABELS.FIX,
    Icon: Wrench,
    color: "orange",
    // The badge's orange is the warning colour.
    iconColor: "text-text-warning",
    meaning:
      "A handyman service: a dedicated block of time with a Birdwatch technician for the home's to-dos.",
  },
};

/**
 * A work type's meaning in the house tooltip. `children` hold its `TooltipTrigger`, `asChild` over
 * the type's chip. It opens on hover, and on keyboard focus when the trigger takes focus.
 */
function WorkTypeMeaningTooltip({
  workType,
  children,
}: {
  workType: ListItemWorkTypeValue;
  children: React.ReactNode;
}) {
  return (
    <Tooltip delayDuration={200}>
      {children}
      <TooltipContent className="max-w-72">
        {WORK_TYPE_SPECS[workType].meaning}
      </TooltipContent>
    </Tooltip>
  );
}

type ListItemWorkTypeBadgeProps = {
  workType: ListItemWorkTypeValue;
  /**
   * A keyboard stop of its own, where the chip stands alone (the side panel's Type row, the
   * member's item drawer), so focus shows the meaning too. Off inside a card's link or button:
   * one more stop per card would be noise, and the meaning shows there on hover.
   */
  focusable?: boolean;
  className?: string;
};

/** A work type's chip: its icon and name in its colour, with its meaning in a tooltip. */
export function ListItemWorkTypeBadge({
  workType,
  focusable = false,
  className,
}: ListItemWorkTypeBadgeProps) {
  const { label, Icon, color } = WORK_TYPE_SPECS[workType];
  return (
    <WorkTypeMeaningTooltip workType={workType}>
      <TooltipTrigger asChild>
        <Badge
          color={color}
          size="xs"
          tabIndex={focusable ? 0 : undefined}
          data-test={`list-item-work-type-${workType.toLowerCase()}`}
          className={cn(
            /* sorted */
            "text-xs",
            focusable &&
              "focus-visible:outline-2 focus-visible:outline-outline-dark-max focus-visible:outline-offset-1",
            className,
          )}
        >
          <Icon size={16} aria-hidden="true" />
          {label}
        </Badge>
      </TooltipTrigger>
    </WorkTypeMeaningTooltip>
  );
}

type ListItemFieldTagsProps = {
  /** As the API sends it: null until the AI has classified the item. */
  workType: string | null | undefined;
  /** Already chosen for the audience (PROPOSED_BY_MEMBER_LABELS or PROPOSED_BY_OFFICE_LABELS). */
  proposedByLabel: string | null;
  /** The type chip is a keyboard stop of its own: only where the tags stand alone (see `ListItemWorkTypeBadge`). */
  focusableWorkType?: boolean;
  className?: string;
};

/** The type chip and the "proposed by" label under a list item's title. Renders nothing when both are absent. */
export function ListItemFieldTags({
  workType,
  proposedByLabel,
  focusableWorkType = false,
  className,
}: ListItemFieldTagsProps) {
  const knownWorkType = isListItemWorkType(workType) ? workType : null;
  if (!knownWorkType && !proposedByLabel) return null;
  return (
    <div
      className={cn(
        /* sorted */
        "flex",
        "flex-wrap items-center gap-1",
        className,
      )}
    >
      {knownWorkType && (
        <ListItemWorkTypeBadge
          workType={knownWorkType}
          focusable={focusableWorkType}
        />
      )}
      {proposedByLabel && (
        // Neutral: purple means In progress on the status badge beside it.
        <Badge color="black" size="xs" className="text-xs">
          {proposedByLabel}
        </Badge>
      )}
    </div>
  );
}
